/**
 * @fileoverview Archive and Restore dialog for an Active Client.
 * @description Archiving (POST /sales/clients/{tenantId}/deactivate) cuts every user of the bank
 * off at their next request (`403 / 4002`), so the dialog says so before it asks; restoring
 * (POST .../restore) lets them back in. Both are Super Admin only, need a fresh sign-in, and take no
 * body. The command's idempotency key outlives the dialog, so a retry after an unknown outcome
 * replays it. A client that is already in the state asked for (`409 / 4126`, `409 / 4127`) or no
 * longer exists (`404 / 4125`) shows the backend's message. Close is held while the request is in
 * flight, and the confirmation runs from the mutation itself, so it survives an unmount. The parent
 * mounts it only while it's open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useArchiveClient } from '@/features/sales/hooks/useArchiveClient'
import { useRestoreClient } from '@/features/sales/hooks/useRestoreClient'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import SalesRequestError from './SalesRequestError'

/** @description Which change the dialog makes. */
export type ClientArchiveMode = 'archive' | 'restore'

interface ClientArchiveDialogProps {
  mode: ClientArchiveMode
  tenantId: string
  bankName: string
  onClose: () => void
  onDone: (mode: ClientArchiveMode, bankName: string) => void
}

/**
 * @component
 * @param {ClientArchiveDialogProps} props - The change, the client and the handlers.
 * @param {ClientArchiveMode} props.mode - Archive or restore.
 * @param {string} props.tenantId - The client to change.
 * @param {string} props.bankName - The bank's name.
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 * @param {(mode: ClientArchiveMode, bankName: string) => void} props.onDone - Called once the
 * change is saved.
 */
function ClientArchiveDialog({
  mode,
  tenantId,
  bankName,
  onClose,
  onDone,
}: ClientArchiveDialogProps) {
  const { t } = useTranslation()
  const archive = useArchiveClient(
    /**
     * @description Reports the archive to the page, even if this dialog has unmounted.
     */
    () => onDone('archive', bankName),
  )
  const restore = useRestoreClient(
    /**
     * @description Reports the restore to the page, even if this dialog has unmounted.
     */
    () => onDone('restore', bankName),
  )
  const change = mode === 'archive' ? archive : restore
  const close = useGuardedClose(onClose, change.isPending)

  /**
   * @description Sends the archive or restore for this client.
   */
  const confirm = () => change.mutate(tenantId)
  const testPrefix = `${mode}-client`

  return (
    <Modal
      open
      onClose={close}
      data-testid={`${testPrefix}-dialog`}
      title={t(`sales.activeClients.${mode}.title`)}
      subtitle={bankName}
    >
      <div className="flex flex-col gap-4">
        <p className="text-compact-lg text-ink" data-testid={`${testPrefix}-message`}>
          {t(`sales.activeClients.${mode}.message`, { bank: bankName })}
        </p>
        {change.isError && <SalesRequestError error={change.error} />}
        <DialogActions
          testIdPrefix={testPrefix}
          cancelLabel={t('sales.pipeline.form.cancel')}
          confirmLabel={t(`sales.activeClients.${mode}.submit`)}
          onCancel={close}
          onConfirm={confirm}
          isLoading={change.isPending}
          disabled={false}
        />
      </div>
    </Modal>
  )
}

export default ClientArchiveDialog
